/**
 * Centralized Error Handling for API Requests
 */

import { AxiosError } from 'axios';
import { ZodError } from 'zod';
import type { ApiError } from '@/types/api';

export class ApiException extends Error {
  public readonly code: string;
  public readonly details: Record<string, unknown>;

  constructor(message: string, code: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = 'ApiException';
    this.code = code;
    this.details = details;
  }
}

/**
 * Parse error response from API
 */
export const parseApiError = (error: unknown): ApiException => {
  // Handle Axios error
  if (error instanceof AxiosError) {
    const data = error.response?.data as ApiError | undefined;

    if (data && !data.success && 'error' in data) {
      return new ApiException(
        data.error.message,
        data.error.code ?? `HTTP_${error.response?.status}`,
        { ...data.error.details, httpStatus: error.response?.status }
      );
    }

    // Network error or no response body
    if (error.code === 'ECONNABORTED') {
      return new ApiException('Request timeout', 'TIMEOUT_ERROR');
    }

    if (!error.response) {
      return new ApiException(
        'Network error. Please check your connection.',
        'NETWORK_ERROR'
      );
    }

    // Server error with status code
    return new ApiException(
      error.response.statusText || 'Server error',
      `HTTP_${error.response.status}`,
      { status: error.response.status }
    );
  }

  // Handle generic error
  if (error instanceof Error) {
    return new ApiException(error.message, 'UNKNOWN_ERROR');
  }

  return new ApiException('An unexpected error occurred', 'UNKNOWN_ERROR');
};

/**
 * Check if error is an API exception
 */
export const isApiException = (error: unknown): error is ApiException => {
  return error instanceof ApiException;
};

/**
 * Get user-friendly error message
 */
export const getErrorMessage = (error: unknown): string => {
  // Handle Zod validation errors
  if (error instanceof ZodError) {
    // Return the first error message from the issues array
    return error.issues[0]?.message || 'Validation error';
  }

  if (isApiException(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
};
